import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import parse from 'html-react-parser';
import { Dispatch, MouseEvent, SetStateAction, useReducer, useState } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import zeplinColor from 'theme/Color';
import CustomDeleteModal from '../../CustomDeleteModal/CustomDeleteModal';
import CustomPopOver from '../../CustomPopOver/CustomPopOver';
import styles from './styles.module.css';

const CustomNoteCard = ({
  data,
  setNotes,
  setSelectedNoteId,
  deleteNotes,
  loading,
}: {
  data: any;
  setNotes: Dispatch<SetStateAction<string>>;
  setSelectedNoteId: Dispatch<SetStateAction<string>>;
  deleteNotes: (id: string) => Promise<void>;
  loading: boolean;
}) => {
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    delete: false,
  });

  const [anchorEl, setAnchorEl] = useState<HTMLImageElement | null>(null);
  const handleClick = (event: MouseEvent<HTMLImageElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const theme = useTheme();

  const handleEditNote = () => {
    setSelectedNoteId(data?.id);
    setNotes(data?.content);
    setAnchorEl(null);
  };
  const handleDeleteNote = () => {
    modalDispatch({ type: 'CLOSE', payload: 'delete' });
    deleteNotes(data.id);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '10px', xl: '16px' } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Box
            sx={{
              borderRadius: '100px',
              padding: '10px',
              width: { xs: '30px', xl: '36px' },
              height: { xs: '30px', xl: '36px' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: zeplinColor.Brand1,
            }}
          >
            {loading ? (
              <Skeleton variant='rounded' width={26} height={26} />
            ) : (
              <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                {data?.createdByName
                  .split(' ')
                  .map((value: any, index: number) =>
                    data?.title?.length &&
                    (index === 0 || index === data?.title.split(' ').length - 1)
                      ? value[0]
                      : '',
                  )}
              </Typography>
            )}
          </Box>
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '1rem',
              }}
            />
          ) : (
            <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
              {data?.createdByName}
            </Typography>
          )}
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '1rem',
              }}
            />
          ) : (
            <Typography variant='responsiveLabel1' color={zeplinColor.Neutral8}>
              {new Date(data?.createdDate)
                .toLocaleString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
                .replace(/(\s)(\w{3})(\s)(\d{4})/, '$1$2, $4')}{' '}
              at{' '}
              {new Date(data?.createdDate).toLocaleString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </Typography>
          )}
        </Box>
        <img
          className={styles['vertical-dots']}
          src={'/assets/vertical-dots.svg'}
          alt='options-icon'
          onClick={handleClick}
          aria-hidden='false'
        />
      </Box>
      {loading ? (
        <Skeleton
          variant='text'
          sx={{
            fontSize: theme.typography.body2,
            width: '100%',
          }}
        />
      ) : (
        <Typography
          variant='body3'
          color={zeplinColor.BasicBlack}
          sx={{
            whiteSpace: 'wrap',
            textWrap: 'wrap',
            flexWrap: 'wrap',
            wordWrap: 'break-word',
            width: '85%',
          }}
        >
          {parse(data?.content)}
        </Typography>
      )}

      <CustomPopOver
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        id={id}
        sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '10px', xl: '16px' },
            padding: { xs: '10px', xl: '16px' },
          }}
        >
          <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleEditNote}>
            <img src={'/assets/edit.svg'} className={styles['option-icon']} alt='edit icon' />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Edit
            </Typography>
          </Box>

          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={() => {
              modalDispatch({ type: 'OPEN', payload: 'delete' });
              setAnchorEl(null);
            }}
          >
            <img
              src={'/assets/delete-red.png'}
              className={styles['option-icon']}
              alt='delete icon'
            />
            <Typography variant='responsiveBody3' color={zeplinColor.Error}>
              Delete
            </Typography>
          </Box>
        </Box>
      </CustomPopOver>

      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={() => handleDeleteNote()}
        title={''}
      />
    </Box>
  );
};

export default CustomNoteCard;
